/**
 * `navigate()` wrapped in a View Transition (#1505 §4 rows 3 and 10).
 *
 * The app mounts `<BrowserRouter>`, which ignores React Router's
 * `viewTransition` option (only the data routers act on it), so a
 * route change that should cross-fade goes through this hook instead.
 * The router commits location changes inside `startTransition`, which
 * `flushSync` cannot force, so the transition's update waits for the
 * commit: the calling component unmounting, or its location changing,
 * whichever comes first, capped at `ROUTE_COMMIT_WAIT_MS` so a slow
 * lazy chunk never freezes the old snapshot on screen. Reduced motion
 * navigates without a transition.
 */
import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react'
import { useLocation, useNavigate, type NavigateOptions, type To } from 'react-router-dom'
import { runViewTransition } from '../lib/transitions'

export const ROUTE_COMMIT_WAIT_MS = 300

function settle(pending: RefObject<(() => void) | null>) {
  pending.current?.()
  pending.current = null
}

export function useTransitionNavigate(): (to: To, options?: NavigateOptions) => void {
  const navigate = useNavigate()
  const location = useLocation()
  const settleRef = useRef<(() => void) | null>(null)

  // Layout effects, not passive ones: both run inside the commit that
  // swaps the route, so the transition captures the new page.
  useLayoutEffect(() => settle(settleRef), [location.key])
  useLayoutEffect(() => () => settle(settleRef), [])

  return useCallback(
    (to: To, options?: NavigateOptions) => {
      runViewTransition(
        () =>
          new Promise<void>((resolve) => {
            const timer = window.setTimeout(resolve, ROUTE_COMMIT_WAIT_MS)
            settleRef.current = () => {
              window.clearTimeout(timer)
              resolve()
            }
            if (options === undefined) navigate(to)
            else navigate(to, options)
          }),
      )
    },
    [navigate],
  )
}
